import { supabase, formatSupabaseError } from '../../supabase';
import { stampCreatedAt } from '../../utils/dbTimestamps';
import { parseFeatures, formatDate } from './ownerUtils';
import { cachedQuery, invalidateCache } from '../cache/queryCache';

export function billingCycleFromDuration(duration) {
  const map = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly',
    lifetime: 'Lifetime',
  };
  return map[duration?.toLowerCase()] || duration || 'Monthly';
}

export function computeExpiryDate(startDate, billingCycle) {
  if (billingCycle === 'Lifetime') return '2099-12-31';
  const date = new Date(`${startDate}T00:00:00`);
  if (billingCycle === 'Monthly') date.setMonth(date.getMonth() + 1);
  else if (billingCycle === 'Quarterly') date.setMonth(date.getMonth() + 3);
  else if (billingCycle === 'Yearly') date.setFullYear(date.getFullYear() + 1);
  else date.setMonth(date.getMonth() + 1);
  return date.toISOString().slice(0, 10);
}

export function getPriceForPlan(planId, billingCycle, plans = []) {
  const plan = plans.find((p) => p.id === planId);
  if (!plan) return 0;
  if (billingCycle === 'Monthly') return plan.monthlyPrice;
  if (billingCycle === 'Quarterly') return plan.quarterlyPrice;
  if (billingCycle === 'Yearly') return plan.yearlyPrice;
  if (billingCycle === 'Lifetime') return plan.lifetimePrice;
  return plan.monthlyPrice;
}

function mapPlanRow(row) {
  return {
    id: row.id,
    name: row.name,
    monthlyPrice: Number(row.monthly_price) || 0,
    quarterlyPrice: Number(row.quarterly_price) || 0,
    yearlyPrice: Number(row.yearly_price) || 0,
    lifetimePrice: Number(row.lifetime_price) || 0,
    features: row.features || '',
    status: row.status || 'active',
    trialDays: Number(row.trial_days) || 0,
    sortOrder: Number(row.sort_order) || 0,
    activeGyms: 0,
  };
}

function slugifyPlanId(name) {
  const base = String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return base || `plan_${Date.now()}`;
}

function mapSubscriptionRow(row, gymName, plan) {
  const status = row.status || 'active';
  const isTrial = String(status).toLowerCase() === 'trial';
  const planName = row.plan_name || plan?.name || '—';

  // Prefer stored values; fall back to linked SaaS plan so columns are never empty
  let billingCycle = row.billing_cycle || '';
  if (!billingCycle && plan) {
    if (Number(plan.monthlyPrice) > 0) billingCycle = 'Monthly';
    else if (Number(plan.quarterlyPrice) > 0) billingCycle = 'Quarterly';
    else if (Number(plan.yearlyPrice) > 0) billingCycle = 'Yearly';
    else if (Number(plan.lifetimePrice) > 0) billingCycle = 'Lifetime';
    else billingCycle = 'Monthly';
  }
  billingCycle = billingCycleFromDuration(billingCycle) || 'Monthly';

  const storedPrice = Number(row.price);
  const planPrice = plan ? getPriceForPlan(plan.id, billingCycle, [plan]) : 0;
  const price = Number.isFinite(storedPrice) && storedPrice > 0
    ? storedPrice
    : (planPrice || (Number.isFinite(storedPrice) ? storedPrice : 0));

  return {
    id: row.id,
    gymId: row.gym_id,
    gym: gymName || '—',
    planId: row.plan_id || plan?.id || '',
    planName,
    selectedPlanName: planName,
    billingCycle,
    price,
    startDate: formatDate(row.start_date),
    expiryDate: formatDate(row.expiry_date || row.trial_ends_at),
    status,
    source: row.source || 'manual',
    contactEmail: row.contact_email || '',
    isTrial,
    trialEndsAt: row.trial_ends_at ? formatDate(row.trial_ends_at) : '',
  };
}

async function resolveGymId(gymName) {
  const trimmed = gymName?.trim();
  if (!trimmed) throw new Error('Gym name is required.');

  const { data: existing } = await supabase
    .from('gyms')
    .select('id')
    .ilike('name', trimmed)
    .maybeSingle();

  if (existing?.id) return existing.id;

  const { data: created, error } = await supabase
    .from('gyms')
    .insert(stampCreatedAt({ name: trimmed, contact_email: null, status: 'active' }))
    .select('id')
    .single();

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not create gym.'));
  }
  return created.id;
}

export async function fetchSaasPlans() {
  return cachedQuery('owner:saas-plans', async () => {
  const { data, error } = await supabase
    .from('saas_plans')
    .select('*')
    .order('sort_order', { ascending: true });

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not load plans.'));
  }
  return (data || []).map(mapPlanRow);
  }, 5 * 60_000);
}

/** Active plans with unique names for dropdowns / signup (hides duplicate package rows). */
export async function fetchSaasPlansForSelect() {
  const plans = await fetchSaasPlans();
  const seen = new Set();
  return plans.filter((plan) => {
    if (plan.status && plan.status !== 'active') return false;
    const key = String(plan.name || '').trim().toLowerCase();
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export async function fetchGymSubscriptions() {
  return cachedQuery('owner:gym-subscriptions', async () => {
  const { data, error } = await supabase
    .from('gym_subscriptions')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not load subscriptions.'));
  }

  const rows = data || [];
  const gymIds = [...new Set(rows.map((r) => r.gym_id).filter(Boolean))];
  const planIds = [...new Set(rows.map((r) => r.plan_id).filter(Boolean))];

  let gymsById = new Map();
  if (gymIds.length) {
    const { data: gyms } = await supabase.from('gyms').select('id, name').in('id', gymIds);
    gymsById = new Map((gyms || []).map((g) => [g.id, g.name]));
  }

  let plansById = new Map();
  if (planIds.length) {
    const { data: plans } = await supabase.from('saas_plans').select('*').in('id', planIds);
    plansById = new Map((plans || []).map((p) => [p.id, mapPlanRow(p)]));
  }

  // Also resolve by plan name when plan_id is missing / stale
  if (!plansById.size || rows.some((r) => !plansById.has(r.plan_id))) {
    const { data: allPlans } = await supabase.from('saas_plans').select('*');
    (allPlans || []).forEach((p) => {
      const mapped = mapPlanRow(p);
      if (!plansById.has(mapped.id)) plansById.set(mapped.id, mapped);
    });
  }

  const plansByName = new Map(
    [...plansById.values()].map((p) => [String(p.name || '').trim().toLowerCase(), p]),
  );

  return rows.map((row) => {
    const plan = plansById.get(row.plan_id)
      || plansByName.get(String(row.plan_name || '').trim().toLowerCase())
      || null;
    return mapSubscriptionRow(row, gymsById.get(row.gym_id), plan);
  });
  }, 60_000);
}

export async function createGymSubscription(form, plans = []) {
  const gymId = await resolveGymId(form.gym);
  const plan = plans.find((p) => p.id === form.planId);
  const billingCycle = billingCycleFromDuration(form.billingCycle);
  const startDate = form.startDate || new Date().toISOString().slice(0, 10);

  const row = {
    gym_id: gymId,
    plan_id: form.planId,
    plan_name: form.planName || plan?.name || 'Starter',
    billing_cycle: billingCycle,
    price: Number(form.price) || getPriceForPlan(form.planId, billingCycle, plans),
    start_date: startDate,
    expiry_date: form.expiryDate || computeExpiryDate(startDate, billingCycle),
    status: form.status || 'active',
    source: 'manual',
    contact_email: form.contactEmail?.trim() || null,
  };

  const { data, error } = await supabase.from('gym_subscriptions').insert(stampCreatedAt(row)).select('*').single();
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not create subscription.'));
  }

  if (row.contact_email) {
    try {
      const { syncGymContactEmailToAdmin } = await import('./gymAdminsApi');
      await syncGymContactEmailToAdmin(gymId, row.contact_email);
    } catch {
      // Subscription still saved; admin email sync is best-effort
    }
  }

  invalidateCache('owner:');
  const { data: gym } = await supabase.from('gyms').select('name').eq('id', gymId).maybeSingle();
  return mapSubscriptionRow(data, gym?.name, plan || null);
}

export async function updateGymSubscription(id, form, plans = []) {
  let gymId = form.gymId;
  if (form.gym) {
    gymId = await resolveGymId(form.gym);
  }

  const plan = plans.find((p) => p.id === form.planId);
  const billingCycle = billingCycleFromDuration(form.billingCycle);

  const row = {
    gym_id: gymId,
    plan_id: form.planId,
    plan_name: form.planName || plan?.name,
    billing_cycle: billingCycle,
    price: Number(form.price) || 0,
    start_date: form.startDate,
    expiry_date: form.expiryDate,
    status: form.status,
    contact_email: form.contactEmail?.trim() || null,
  };

  const { data, error } = await supabase
    .from('gym_subscriptions')
    .update(row)
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not update subscription.'));
  }

  if (row.contact_email && gymId) {
    try {
      const { syncGymContactEmailToAdmin } = await import('./gymAdminsApi');
      await syncGymContactEmailToAdmin(gymId, row.contact_email);
    } catch {
      // Subscription still saved; admin email sync is best-effort
    }
  }

  invalidateCache('owner:');
  const { data: gym } = await supabase.from('gyms').select('name').eq('id', gymId).maybeSingle();
  return mapSubscriptionRow(data, gym?.name || form.gym, plan || null);
}

export async function deleteGymSubscription(id) {
  const { error } = await supabase.from('gym_subscriptions').delete().eq('id', id);
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not delete subscription.'));
  }
  invalidateCache('owner:');
}

export async function fetchSaasPlanById(id) {
  const { data, error } = await supabase.from('saas_plans').select('*').eq('id', id).maybeSingle();
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not load plan.'));
  }
  return data ? mapPlanRow(data) : null;
}

export async function updateSaasPlan(id, form) {
  const row = {
    name: form.name?.trim(),
    monthly_price: Number(form.monthlyPrice ?? form.price) || 0,
    quarterly_price: Number(form.quarterlyPrice) || 0,
    yearly_price: Number(form.yearlyPrice) || 0,
    lifetime_price: Number(form.lifetimePrice) || 0,
    features: form.features || '',
    status: form.status || 'active',
    trial_days: Number(form.trialDays) || 0,
    sort_order: Number(form.sortOrder) || 0,
  };

  const { data, error } = await supabase.from('saas_plans').update(row).eq('id', id).select('*').single();
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not update plan.'));
  }
  invalidateCache('owner:');
  return mapPlanRow(data);
}

export async function createSaasPlan(form) {
  const name = form.name?.trim();
  if (!name) throw new Error('Plan name is required.');

  const { data: sameName } = await supabase
    .from('saas_plans')
    .select('id, name')
    .ilike('name', name);
  if ((sameName || []).some((row) => String(row.name || '').trim().toLowerCase() === name.toLowerCase())) {
    throw new Error('A package with this name already exists. Use a different name or edit the existing package.');
  }

  let id = form.id?.trim() || slugifyPlanId(name);
  const { data: existing } = await supabase.from('saas_plans').select('id').eq('id', id).maybeSingle();
  if (existing?.id) {
    id = `${id}_${Date.now()}`;
  }

  const row = {
    id,
    name,
    monthly_price: Number(form.monthlyPrice) || 0,
    quarterly_price: Number(form.quarterlyPrice) || 0,
    yearly_price: Number(form.yearlyPrice) || 0,
    lifetime_price: Number(form.lifetimePrice) || 0,
    features: form.features || '',
    status: form.status || 'active',
    trial_days: Number(form.trialDays) || 0,
    sort_order: Number(form.sortOrder) || 0,
  };

  const { data, error } = await supabase.from('saas_plans').insert(stampCreatedAt(row)).select('*').single();
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not create plan.'));
  }
  invalidateCache('owner:');
  return mapPlanRow(data);
}

export async function deleteSaasPlan(id) {
  const { count, error: countError } = await supabase
    .from('gym_subscriptions')
    .select('*', { count: 'exact', head: true })
    .eq('plan_id', id);

  if (countError) {
    throw new Error(formatSupabaseError(countError, 'Could not verify plan usage.'));
  }
  if (count > 0) {
    throw new Error('Cannot delete this plan while gyms are subscribed to it. Set status to inactive instead.');
  }

  const { error } = await supabase.from('saas_plans').delete().eq('id', id);
  if (error) {
    throw new Error(formatSupabaseError(error, 'Could not delete plan.'));
  }
  invalidateCache('owner:');
}

export function getPlanById(id, plans) {
  return plans.find((p) => p.id === id);
}
